import { ExportFileType } from '@common/ts/enums';
import { buildUrlWithParams } from '@common/utils';
import { ECategoryMode, TranscriptsView } from '@/enums';
import { FiltersConfig, PaginationConfig, ParamsConfig, SortConfig } from '@/types';

export const API_ENDPOINTS = {
  ORGANIZATIONS: {
    GET_BY_ID: (organizationId?: string) => `/organizations/${organizationId}`,
    UPDATE: (organizationId?: string) => `/organizations/${organizationId}`,
    DELETE: (organizationId: string) => `/organizations/${organizationId}`,
    GET_ALL: '/organizations',
    GET_MEMBERS: (
      organizationId?: string,
      pagination?: PaginationConfig,
      filters?: FiltersConfig,
      sortConfig?: SortConfig,
    ) => {
      const base = `/organizations/${organizationId}/members`;
      const params: ParamsConfig = { ...pagination, ...filters, ...sortConfig };
      return buildUrlWithParams(base, params);
    },
    REMOVE_MEMBER: (organizationId?: string) => `/organizations/${organizationId}/members`,
    UPDATE_MEMBER_ROLE: (organizationId?: string) => `/organizations/${organizationId}/members`,
    INVITE_USER: (organizationId?: string) => `/organizations/${organizationId}/invite`,
    CANCEL_INVITATION: (organizationId?: string) => `/organizations/${organizationId}/invite`,
    VALIDATE_INVITE: (organizationId?: string) =>
      `/organizations/${organizationId}/validate_invite`,
    NOTIFY_INVITER: (organizationId?: string) => `/organizations/${organizationId}/notify_inviter`,
    RESEND_INVITATION: (organizationId?: string) =>
      `/organizations/${organizationId}/resend_invitation`,
    TRANSFER_OWNERSHIP: (organizationId?: string) =>
      `/organizations/${organizationId}/transfer_ownership`,
  },

  WORKSPACES: {
    CREATE: (organizationId?: string) => `/organizations/${organizationId}/workspaces`,
    GET_ALL: (organizationId?: string, pagination?: PaginationConfig) => {
      const base = `/organizations/${organizationId}/workspaces`;
      const params: ParamsConfig = { ...pagination };
      return buildUrlWithParams(base, params);
    },
    GET_BY_ID: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}`,
    UPDATE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}`,
    DELETE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}`,
    GET_MEMBERS: (organizationId?: string, workspaceId?: string, pagination?: PaginationConfig) => {
      const base = `/organizations/${organizationId}/workspaces/${workspaceId}/members`;
      const params: ParamsConfig = { ...pagination };
      return buildUrlWithParams(base, params);
    },
    REMOVE_MEMBER: (organizationId?: string, workspaceId?: string, userId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/members/${userId}`,
    UPDATE_ROLE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/role`,
    SHARE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/share`,
    TRANSFER_OWNERSHIP: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/transfer_ownership`,
  },

  PROJECTS: {
    GET_ALL: (
      organizationId?: string,
      workspaceId?: string,
      pagination?: PaginationConfig,
      filters?: FiltersConfig,
      sortConfig?: SortConfig,
    ) => {
      const base = `/organizations/${organizationId}/workspaces/${workspaceId}/projects`;
      const params: ParamsConfig = { ...pagination, ...filters, ...sortConfig };
      return buildUrlWithParams(base, params);
    },
    CREATE: (organizationId?: string, workspaceId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects`,
    GET_BY_ID: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}`,
    UPDATE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}`,
    DELETE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}`,
    COMPLETE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/complete`,
    MOVE: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/move`,
    COPY: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/copy`,
    PUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/publish`,
    REQUEST_PUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/request_publish`,
    APPROVE_PUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/approve_publish`,
    UPLOAD_MEDIA: (
      organizationId?: string,
      workspaceId?: string,
      projectId?: string,
      questionId?: string,
    ) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/media/${questionId}`,
    DELETE_MEDIA: (
      organizationId?: string,
      workspaceId?: string,
      projectId?: string,
      questionId?: string,
    ) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/media/${questionId}`,
    RENAME: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/rename`,
    UNPUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/unpublish`,
    REPUBLISH: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/republish`,
    GET_CONVERSATION_COUNTS: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/conversations/counts`,
    GET_THEME: (projectId?: string) => `/projects/${projectId}/theme`,
    GET_CONSENT_SCREEN: (projectId?: string) => `/projects/${projectId}/consent_screen`,
    GET_QUOTA_STATUS: (organizationId?: string, workspaceId?: string, projectId?: string) =>
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/quota-status`,
  },

  USERS: {
    REGISTER: '/users/register',
    VERIFY_EMAIL: '/users/verify_email',
    ACCEPT_TELLET_INVITE: (organizationId?: string) =>
      `/users/accept_tellet_invite/${organizationId}`,
    ACCEPT_ORGANIZATION_INVITE: (organizationId?: string) =>
      `/users/accept_organization_invite/${organizationId}`,
    LOGIN: '/users/login',
    RESEND_VERIFICATION_EMAIL: '/users/resend_verification_email',
    GET_USER_DATA: '/users',
    UPDATE_PROFILE: '/users',
    DELETE_ACCOUNT: '/users',
    FORGOT_PASSWORD: '/users/forgot_password',
    RESET_PASSWORD: '/users/reset_password',
    RESET_PASSWORD_PREFLIGHT: '/users/reset_password/preflight',
    CHANGE_PASSWORD: '/users/change_password',
    VERIFY_2FA: '/users/verify-2fa',
    GENERATE_2FA: '/users/generate-2fa',
    ENABLE_2FA: '/users/enable-2fa',
    DISABLE_2FA: '/users/disable-2fa',
  },
  THEMES: {
    GET_BY_ID: (organizationId: string, themeId: string | null) =>
      `/organizations/${organizationId}/themes/${themeId}`,
    UPDATE: (organizationId?: string, themeId?: string) =>
      `/organizations/${organizationId}/themes/${themeId}`,
    DELETE: (organizationId?: string, themeId?: string) =>
      `/organizations/${organizationId}/themes/${themeId}`,
    GET_ALL: (
      organizationId?: string,
      pagination?: PaginationConfig,
      filters?: FiltersConfig,
      sortConfig?: SortConfig,
    ) => {
      const base = `/organizations/${organizationId}/themes`;
      const params: ParamsConfig = { ...pagination, ...filters, ...sortConfig };
      return buildUrlWithParams(base, params);
    },
    CREATE: (organizationId?: string) => `/organizations/${organizationId}/themes`,
  },
  RESULTS: {
    GET_OVERALL: (projectId?: string, filterId?: string | null) =>
      `analyzer/results/${projectId}/overall${filterId ? `?filter_id=${filterId}` : ''}`,
    GET_INTERVIEW_QUESTIONS: (projectId: string, filterId: string | null) =>
      `analyzer/results/${projectId}/interview_questions${
        filterId ? `?filter_id=${filterId}` : ''
      }`,
    GET_PER_QUESTION_INSIGHTS: (projectId: string, questionId: string, filterId: string | null) =>
      `analyzer/results/${projectId}/perquestion/${questionId}/insights${
        filterId ? `?filter_id=${filterId}` : ''
      }`,
    GET_PER_QUESTION_TRANSCRIPTS: (
      projectId?: string,
      questionId?: string,
      categoryType?: ECategoryMode | null,
      filterId?: string | null,
      language?: string | null,
      pagination?: PaginationConfig,
    ) => {
      const base = `analyzer/results/${projectId}/perquestion/${questionId}/transcripts`;
      return buildUrlWithParams(base, {
        filter_id: filterId,
        category_type: categoryType,
        language,
        ...pagination,
      });
    },
    /**
     * Paginated variant of GET_PER_QUESTION_TRANSCRIPTS. Built against
     * the `/transcripts/page` backend route — the non-paginated
     * `/transcripts` route is kept intact for external API consumers
     * (customer scripts using dashboard JWTs).
     */
    GET_PER_QUESTION_TRANSCRIPTS_PAGE: (params: {
      projectId?: string;
      questionId?: string;
      categoryType?: ECategoryMode | null;
      filterId?: string | null;
      language?: string | null;
      page?: number;
      limit?: number;
      search?: string;
      categoryIds?: string[];
      sortBy?: string;
      sortDirection?: 'asc' | 'desc';
    }) => {
      const base = `analyzer/results/${params.projectId}/perquestion/${params.questionId}/transcripts/page`;
      return buildUrlWithParams(base, {
        filter_id: params.filterId,
        category_type: params.categoryType,
        language: params.language,
        page: params.page,
        limit: params.limit,
        search: params.search,
        // Server accepts comma-separated OR repeated; we send
        // comma-separated to keep the URL compact.
        category_ids:
          params.categoryIds && params.categoryIds.length
            ? params.categoryIds.join(',')
            : undefined,
        sort_by: params.sortBy,
        sort_direction: params.sortDirection,
      });
    },
    GET_CONVERSATIONS: (
      projectId?: string,
      filterId?: string | null,
      language?: string | null,
      conversationFilter?: TranscriptsView,
      pagination?: PaginationConfig,
      searchFilters?: FiltersConfig,
      sortConfig?: SortConfig,
    ) => {
      const base = `analyzer/results/${projectId}/conversations`;
      return buildUrlWithParams(base, {
        filter_id: filterId,
        language,
        conversationFilter,
        ...pagination,
        ...searchFilters,
        ...sortConfig,
      });
    },
    GET_CONVERSATION_BY_ID: (projectId?: string, conversationId?: string) =>
      `analyzer/results/${projectId}/conversations/${conversationId}`,
    DELETE_CONVERSATIONS: (projectId?: string) => `analyzer/results/${projectId}/conversations`,
    MARK_CONVERSATIONS_COMPLETE: (projectId?: string) =>
      `analyzer/results/${projectId}/conversations/mark-complete`,
    GET_LANGUAGES: (projectId?: string) => `analyzer/results/${projectId}/languages`,
    GET_TRANSLATION_PROGRESS: (projectId?: string, language?: string) =>
      `analyzer/results/${projectId}/languages/${language}/progress`,
    TRANSLATE_CONVERSATIONS: (projectId?: string) => `analyzer/results/${projectId}/languages`,
    EXPORT_CONVERSATION: (
      projectId?: string,
      conversationId?: string,
      fileType?: ExportFileType,
      language?: string,
    ) =>
      buildUrlWithParams(
        `analyzer/results/single_conversation_details/export/${projectId}/${conversationId}`,
        {
          file_type: fileType,
          language,
        },
      ),
    EXPORT_CONVERSATION_DETAILS: (
      projectId: string,
      filterId: string | null,
      fileType?: ExportFileType,
      transcriptsView?: TranscriptsView,
    ) => {
      return buildUrlWithParams(`analyzer/results/conversations_details/export/${projectId}`, {
        file_type: fileType,
        filter_id: filterId,
        conversationFilter: transcriptsView,
      });
    },
    EXPORT_CONVERSATION_OVERVIEW: (
      projectId: string,
      filterId: string | null,
      fileType?: ExportFileType,
      transcriptsView?: TranscriptsView,
    ) => {
      return buildUrlWithParams(`analyzer/results/conversations_overview/export/${projectId}`, {
        file_type: fileType,
        filter_id: filterId,
        conversationFilter: transcriptsView,
      });
    },
    EXPORT_FULL_ANSWERS_TRANSCRIPT: (
      projectId: string,
      filterId: string | null,
      fileType?: ExportFileType,
      transcriptsView?: TranscriptsView,
    ) => {
      return buildUrlWithParams(`analyzer/results/questions-columns/export/${projectId}`, {
        file_type: fileType,
        filter_id: filterId,
        conversationFilter: transcriptsView,
      });
    },
    EXPORT_ANALYSIS: (projectId: string, filterId?: string | null) => {
      return buildUrlWithParams(`analyzer/results/analysis/export/${projectId}`, {
        filter_id: filterId,
      });
    },
    GET_CONVERSATION_PANEL: (projectId?: string, conversationId?: string) =>
      `analyzer/results/${projectId}/conversations/${conversationId}/panel`,
    EXPORT_CONVERSATION_MEDIA: (projectId: string, conversationId: string) =>
      `analyzer/results/media/export/${projectId}/${conversationId}`,
    GET_SCREENING_STATS: (projectId: string) => `analyzer/results/screening-stats/${projectId}`,
  },
  RUN: {
    START_ANALYSIS: (projectId?: string, filter_id?: string, force = false) => {
      // Query params: filter_id (optional), force (optional bool).
      // `force=true` bypasses the "no new conversations" no-op so we
      // can upgrade old-pipeline projects to the new schema (used by
      // the outdated-analysis gate in RA + Compare).
      const params = new URLSearchParams();
      if (filter_id) params.set('filter_id', filter_id);
      if (force) params.set('force', 'true');
      const qs = params.toString();
      return `analyzer/run/${projectId}${qs ? `?${qs}` : ''}`;
    },
    GET_RUN_STATE: (projectId?: string) => `analyzer/run/${projectId}/state`,
    /**
     * D.8: new endpoint added by the Wave A backend agent.
     * Returns { phase, current, total, started_at, elapsed_ms, completed_at }.
     * Supports ETag/304 or 2s Redis-TTL so multi-tab polling is cheap.
     */
    GET_RUN_PROGRESS: (projectId?: string) => `analyzer/run/${projectId}/progress`,
  },
  RESEARCH_ASSISTANT: {
    ANALYSIS_SCHEMA_STATUS: (projectId: string, filterId?: string | null) =>
      `analyzer/research-assistant/${projectId}/analysis-schema-status${filterId ? `?filter_id=${filterId}` : ''}`,
    LIST_THREADS: (projectId: string, filterId?: string | null) =>
      `analyzer/research-assistant/${projectId}/threads${filterId ? `?filter_id=${filterId}` : ''}`,
    CREATE_THREAD: (projectId: string) => `analyzer/research-assistant/${projectId}/threads`,
    GET_THREAD: (projectId: string, threadId: string) =>
      `analyzer/research-assistant/${projectId}/threads/${threadId}`,
    DELETE_THREAD: (projectId: string, threadId: string) =>
      `analyzer/research-assistant/${projectId}/threads/${threadId}`,
    UPDATE_THREAD: (projectId: string, threadId: string) =>
      `analyzer/research-assistant/${projectId}/threads/${threadId}`,
    CHAT: (projectId: string, threadId: string) =>
      `analyzer/research-assistant/${projectId}/threads/${threadId}/chat`,
    SUGGESTIONS: (projectId: string, filterId?: string | null) =>
      `analyzer/research-assistant/${projectId}/suggestions${filterId ? `?filter_id=${filterId}` : ''}`,
  },
  AI_SEARCH: {
    GET: (projectId?: string, filterId?: string | null) =>
      `analyzer/aisearch/${projectId}${filterId ? `?filter_id=${filterId}` : ''}`,
    CREATE: (projectId?: string) => `analyzer/aisearch/${projectId}`,
    DELETE: (projectId?: string) => `analyzer/aisearch/${projectId}`,
  },
  CATEGORIES: {
    GENERATE: (projectId?: string, categoryType?: string, conversationsNumber?: number) =>
      `analyzer/categorization/${projectId}?category_type=${categoryType}&conversations_num=${conversationsNumber}`,
    GENERATE_AI: (projectId?: string, questionId?: string) =>
      `analyzer/categorization/${projectId}/ai?question_id=${questionId}`,
    GET_BY_QUESTION: (projectId?: string, questionId?: string, filter_id?: string | null) =>
      `analyzer/categorization/${projectId}/question/${questionId}${
        filter_id ? `?filter_id=${filter_id}` : ''
      }`,
    DELETE_BY_QUESTION: (projectId?: string, questionId?: string) =>
      `analyzer/categorization/${projectId}/question/${questionId}`,
    UPDATE_BY_QUESTION: (projectId?: string, questionId?: string) =>
      `analyzer/categorization/${projectId}/question/${questionId}`,
  },
  NOTIFICATIONS: {
    GET_PREFERENCES: '/users/notifications/preferences',
    UPDATE_PREFERENCES: '/users/notifications/preferences',
  },
  FILTERS: {
    CREATE: (projectId?: string) => `analyzer/filters/${projectId}`,
    GET_BY_ID: (projectId?: string) => `analyzer/filters/${projectId}`,
    DELETE: (projectId?: string, filterId?: string) => `analyzer/filters/${projectId}/${filterId}`,
    UPDATE: (projectId?: string, filterId?: string) => `analyzer/filters/${projectId}/${filterId}`,
    GET_METADATA: (projectId?: string) => `analyzer/filters/${projectId}/metadata`,
    /**
     * Project-level respondent_index → conversation_id lookup.
     * Used by the Research Assistant citation pills so `[R33]` opens
     * the matching transcript without a per-click round-trip.
     */
    GET_RESPONDENT_LOOKUP: (projectId?: string) =>
      `analyzer/filters/${projectId}/respondents/lookup`,
  },
  COMPARISON: {
    START: (projectId: string) => `analyzer/comparison/${projectId}`,
    STATUS: (projectId: string, comparisonId: string) =>
      `analyzer/comparison/${projectId}/${comparisonId}/status`,
    LIST: (projectId: string) => `analyzer/comparison/${projectId}/list`,
    DELETE: (projectId: string, comparisonId: string) =>
      `analyzer/comparison/${projectId}/${comparisonId}`,
    RECOMPUTE_TIER: (projectId: string, comparisonId: string) =>
      `analyzer/comparison/${projectId}/${comparisonId}/recompute-tier`,
  },
};
